import type { ElementType, MonsterInstance, MonsterSpecies, Rarity, RealmId } from './domain.js';

// ── Off-chain Gold economy + P2P market + quests + sinks (Kintara model). ──

/** A P2P listing: seller sells Gold/item/monster, priced in $TOKEN. seller 95% / treasury 5%. */
export interface MarketListing {
  id: string;
  sellerId: string;
  sellerWallet: string;
  kind: 'gold' | 'item' | 'monster';
  /** itemId / speciesId / null for gold */
  refId: string | null;
  qty: number;
  /** asking price in $TOKEN (UI units) */
  priceToken: number;
  status: 'open' | 'sold' | 'cancelled';
  createdAt: string;
}

export const MARKET_SELLER_BPS = 9500; // 95% to seller
export const MARKET_TREASURY_BPS = 500; // 5% to treasury

/** Daily quest definition + a player's progress. */
export interface QuestDef {
  id: string;
  title: string;
  description: string;
  realm: RealmId;
  goal: { kind: 'catch' | 'defeat' | 'gather' | 'visit'; target: string; count: number };
  reward: { gold: number; xp: number };
}
export interface QuestProgress {
  questId: string;
  playerId: string;
  progress: number;
  completed: boolean;
  claimedAt: string | null;
  dayKey: string; // YYYY-MM-DD reset boundary
}

/** Spinner: pay $TOKEN, 50% burned / 50% treasury, no token payout (prizes are in-game). */
export const SPINNER_BURN_BPS = 5000;
export const SPINNER_TREASURY_BPS = 5000;

// ── Card Shop: real CollectorCrypt cards ordered with Gold, fulfilled by the team. ──

/** Gold per $1 of a card's real value. Raised (315→600) so redeeming real cards costs ~2× more Gold —
 *  protects the treasury (players can't drain cheap cards) and makes a real card a meaningful goal. */
export const GOLD_PER_USD = 600;

/** Cheapest a card can ever cost — a floor so a mis-priced / cheap listing can't show a silly price
 *  (CollectorCrypt occasionally returns $1 for valuable cards). ≈ $9.5 at GOLD_PER_USD. */
export const MIN_CARD_GOLD = 5700;
/** Cards whose real value is below this are hidden from the shop (data errors / too cheap to list). */
export const MIN_CARD_USD = MIN_CARD_GOLD / GOLD_PER_USD;

/** In-game Gold price for a card of a given real USD value (never below the floor). */
export function cardGoldPrice(valueUsd: number): number {
  return Math.max(MIN_CARD_GOLD, Math.round((Number(valueUsd) || 0) * GOLD_PER_USD));
}

// ── Gacha packs: order a CollectorCrypt mystery pack (team-fulfilled), priced like cards. ──
/** Gacha pack tiers, in EUR (≈ the real pack value). Priced in Gold ∝ value, like single cards. */
export const GACHA_TIERS = [50, 250, 2500] as const;
/** Card families a gacha pack can be drawn from. */
export const GACHA_CATEGORIES = ['Pokemon', 'One Piece', 'Sports'] as const;
/** Gold price for a gacha tier (its EUR value pegged through GOLD_PER_USD, floored like cards). */
export function gachaGoldPrice(tierEur: number): number {
  return Math.max(MIN_CARD_GOLD, Math.round((Number(tierEur) || 0) * GOLD_PER_USD));
}

// ── $TOKEN / SOL top-ups, boosters, and gacha cash prices (INBOUND payments → treasury). ──
// PLACEHOLDER prices — the owner TUNES these to the live token's market value. Token prices are in
// WHOLE tokens (the mint has 6 decimals); SOL prices are in SOL. These feed the inbound-verify rail
// (player sends → server verifies the amount received ≥ the price → grants).

// ── Cash top-ups: prices are anchored in USD; the $TOKEN/SOL amount is computed DYNAMICALLY from the
//    live market price at purchase time (usdToToken / usdToSol). This is the critical safety property:
//    buying Gold for cash must never be cheaper than the real-world value of the cards that Gold can
//    redeem (1 Gold = $1/GOLD_PER_USD), or the treasury bleeds. ──

/** A Gold pack the player can buy with $TOKEN or SOL (instant in-game Gold). USD-anchored. */
export interface TopupPack {
  id: string;
  gold: number;
}
/** Margin charged ON TOP of the gold's redemption value when buying Gold for cash. The base alone
 *  already makes 1M Gold ≈ $1,667 (≈ the value of the cards it redeems); the markup adds team margin. */
export const TOPUP_MARKUP_PCT = 10;
/** Launch PROMO: every cash price (packs / booster / gacha) is charged at −PROMO% off its regular
 *  price, so buying is visibly advantageous. The UI shows the regular price struck through. */
export const TOPUP_PROMO_PCT = 10;
/** Apply the launch promo to a regular USD price → the actual charged USD price. */
export function applyPromo(regularUsd: number): number {
  return regularUsd * (1 - TOPUP_PROMO_PCT / 100);
}
export const TOPUP_PACKS: readonly TopupPack[] = Object.freeze([
  { id: 'gold_small', gold: 10_000 },
  { id: 'gold_medium', gold: 50_000 },
  { id: 'gold_large', gold: 150_000 },
]);
export function getTopupPack(id: string): TopupPack | undefined {
  return TOPUP_PACKS.find((p) => p.id === id);
}
/** USD price of a Gold pack: the gold's redemption value (gold ÷ GOLD_PER_USD) × the markup. */
export function topupUsd(pack: TopupPack): number {
  return (pack.gold / GOLD_PER_USD) * (1 + TOPUP_MARKUP_PCT / 100);
}

/** A pay-to-win Gold-earn booster: a temporary multiplier on battle Gold for N battles. USD-priced. */
export interface GoldBoosterDef {
  id: string;
  multiplier: number; // e.g. 2 = double battle Gold
  battles: number; // how many wins it lasts
  usd: number; // flat USD price (a play advantage, not gold-redemption-valued) — tune freely
}
export const GOLD_BOOSTER: GoldBoosterDef = Object.freeze({
  id: 'gold_2x_50',
  multiplier: 2,
  battles: 50,
  usd: 15,
});

/** EUR→USD for the EUR-denominated gacha tiers. Update if the peg drifts materially. */
export const EUR_USD = 1.08;
/** Gacha tier (€) → USD price (the tier IS its EUR value). The $TOKEN/SOL amount is dynamic. */
export function gachaUsd(tierEur: number): number {
  return (Number(tierEur) || 0) * EUR_USD;
}

/** USD → whole-token amount at a live token price (rounded UP so the treasury never under-collects). */
export function usdToToken(usd: number, tokenPriceUsd: number): number {
  if (!(tokenPriceUsd > 0)) return 0;
  return Math.ceil(usd / tokenPriceUsd);
}
/** USD → SOL amount (3-dp, rounded UP) at a live SOL price. */
export function usdToSol(usd: number, solPriceUsd: number): number {
  if (!(solPriceUsd > 0)) return 0;
  return Math.ceil((usd / solPriceUsd) * 1000) / 1000;
}

// ── $MONSTARA holder DISCOUNT: hold the launched token → cheaper real graded cards. ──
// Tiers are checked SERVER-SIDE against the wallet's live on-chain balance (never the client),
// both when listing cards and (critically) again at order time, so the discount can't be forged.

/** A holder discount tier: hold ≥ `minTokens` $MONSTARA (UI units) → `pct`% off card Gold prices. */
export interface HolderDiscountTier {
  minTokens: number;
  pct: number;
}

/** Discount tiers, DESCENDING by minTokens so the first qualifying tier is the best one. */
export const HOLDER_DISCOUNT_TIERS: readonly HolderDiscountTier[] = Object.freeze([
  { minTokens: 1_000_000, pct: 20 },
  { minTokens: 250_000, pct: 10 },
  { minTokens: 50_000, pct: 5 },
]);

/** Highest discount % a holder of `uiAmount` $MONSTARA qualifies for (0 if below every tier). */
export function holderDiscountPct(uiAmount: number): number {
  const amt = Number(uiAmount) || 0;
  for (const tier of HOLDER_DISCOUNT_TIERS) {
    if (amt >= tier.minTokens) return tier.pct;
  }
  return 0;
}

/** Apply a holder discount % to a Gold price (rounded, floored at 1 Gold). */
export function applyHolderDiscount(priceGold: number, pct: number): number {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  return Math.max(1, Math.round((Number(priceGold) || 0) * (1 - p / 100)));
}

/** Gold awarded for winning a battle vs an enemy of the given level. Nerfed (~25%) from 8+lvl*2 so
 *  Gold accrues more slowly — keeps real-card redemptions a deliberate goal, not a quick farm. */
export function battleGoldReward(enemyLevel: number): number {
  return 5 + Math.round(Math.max(1, Math.round(enemyLevel)) * 1.5);
}

// ── Daily-streak multiplier: reward consistent play (gold + XP) to drive retention. ──
// streakDays = consecutive days played (0 = first day). The multiplier scales ALL earned gold + XP.
// Schedule (capped at 3×): day0 1× · day1 1.2× · day2 1.5× · day3 2× · day4 2.5× · day5+ 3×.
/** Hard cap on the COMBINED battle-Gold multiplier (zone × streak × paid booster), so pay-to-win +
 *  a max streak in the Cave can't runaway-inflate Gold. Applied in awardBattleGold. */
export const MAX_GOLD_MULTIPLIER = 6;

// ── Card Vault: stake a real graded-card NFT with us → earn passive Gold (the card stays in our
//    custody until un-stake). Distinct from the in-game "Gold Vault" (daily +100 claim). ──
/** Passive Gold/day for a staked card, by its USD value (FROZEN at stake time). Anchored so a ~$11
 *  (€10) card ≈ 300 Gold/day, scaling SUB-linearly (√) so a $2000 card ≈ ~4047/day — generous on
 *  small/mid cards without the top end exploding (a flat % would make a $2000 card ~55k/day). */
export function vaultDailyGold(valueUsd: number): number {
  const v = Math.max(0, Number(valueUsd) || 0);
  if (v <= 0) return 0;
  return Math.round(300 * Math.sqrt(v / 11));
}
/** Treasury guardrail: max passive Gold credited across ALL stakes per rolling 24h. Tunable. */
export const VAULT_DAILY_GOLD_CAP = 100_000;
/** Minimum days a card must stay staked before un-stake is allowed. 0 = no lockup: players can claim
 *  their Gold daily and request un-stake anytime (un-stakes are still manually processed by the team). */
export const VAULT_LOCKUP_DAYS = 0;
export const STREAK_MULTIPLIERS = [1, 1.2, 1.5, 2, 2.5, 3] as const;
/** Earnings multiplier for a given consecutive-day streak (clamped to the table; caps at 3×). */
export function streakMultiplier(streakDays: number): number {
  const i = Math.max(0, Math.min(STREAK_MULTIPLIERS.length - 1, Math.floor(Number(streakDays) || 0)));
  return STREAK_MULTIPLIERS[i];
}

/** A real graded card available to order, from the CC marketplace catalog. */
export interface ShopCard {
  mint: string;
  name: string;
  grade: string;
  grader: string; // PSA / BGS / CGC …
  valueUsd: number;
  imageUrl: string | null;
  priceGold: number;
  /** $TOKEN price (UI units), pegged through priceGold. Present once the on-chain feature is wired. */
  priceToken?: number;
}

export type CardOrderStatus = 'pending_review' | 'approved' | 'shipped' | 'rejected';

/** Shipping details collected at order time (PII — stored server-side only). */
export interface ShippingAddress {
  name: string;
  address: string;
  city: string;
  postal: string;
  country: string;
}

/**
 * How the player receives a redeemed card:
 *  - 'onchain': the CC card is an NFT → transferred to the order's `wallet` (no shipping/PII).
 *  - 'irl':     physical shipping to the collected `shipping` address.
 */
export type DeliveryMethod = 'onchain' | 'irl';

/** A player's order for a real card, fulfilled manually by the team. */
export interface CardOrder {
  id: string;
  wallet: string;
  cardMint: string;
  cardName: string;
  cardImage: string | null;
  valueUsd: number;
  priceGold: number;
  /** Currency the order was paid in. Defaults to 'gold' for every legacy order. */
  payWith?: 'gold' | 'token';
  /** $TOKEN charged (UI units) when payWith==='token'. */
  priceToken?: number;
  status: CardOrderStatus;
  /** Delivery channel chosen at order time. On-chain → NFT to `wallet`; IRL → ship to `shipping`. */
  deliveryMethod: DeliveryMethod;
  /** Null fields for on-chain orders (no physical address collected). */
  shipping: ShippingAddress;
  /** Player's Twitter/X handle (collected at checkout, both delivery methods) — fulfilment contact. */
  twitter: string | null;
  /** In-game username snapshotted at order time, so the team knows who ordered. */
  playerName: string | null;
  tracking: string | null;
  createdAt: string;
}

// ── Gold Item Shop: a fixed catalog of SYSTEM items bought with in-game Gold. ──
// All prices are tuned as Gold SINKS so the shop can never inflate the economy. The
// Gold Vault is the one "generator" and is deliberately ROI-negative short-term
// (~30-day payback) + hard-capped to +100 per real 24h, enforced server-side.

/** Effects a catalog item can apply when purchased (server-applied, never trusted from the client). */
export type ShopItemEffect =
  | { kind: 'inventory'; itemId: string; qty: number } // grant a consumable stack
  | { kind: 'buff'; buff: 'lucky_charm'; battles: number } // capped temporary battle-Gold boost
  | { kind: 'gold_vault' }; // one-time unlock of the daily-claim Gold Vault

/** A single buyable system item: stable id, display copy, Gold price, and its server effect. */
export interface ShopItemDef {
  id: string;
  name: string;
  description: string;
  priceGold: number;
  effect: ShopItemEffect;
}

/** Lucky Charm: +10% battle Gold, capped to this many battles (a bounded, temporary boost). */
export const LUCKY_CHARM_BATTLES = 20;
export const LUCKY_CHARM_BONUS_PCT = 10;

/** Gold Vault: one-time unlock → claim a small fixed amount once per real 24h. */
export const GOLD_VAULT_CLAIM_AMOUNT = 100;
export const GOLD_VAULT_CLAIM_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** Daily login reward: a free grant every player can claim once per real 24h. */
export const DAILY_REWARD_AMOUNT = 50; // legacy flat fallback
export const DAILY_REWARD_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** One day's reward in the rotating daily-login cycle: either Gold or a stack of an item. */
export type DailyRewardDef =
  | { kind: 'gold'; gold: number; label: string }
  | { kind: 'item'; itemId: string; qty: number; label: string };

/** The rotating cycle — players advance one step per claim and loop. Item ids are real @mn/content
 *  ids (capture balls = it_snare/it_net, potions = it_salve/it_poultice). */
export const DAILY_REWARDS: DailyRewardDef[] = [
  { kind: 'gold', gold: 200, label: '200 Gold' },
  { kind: 'item', itemId: 'it_snare', qty: 3, label: '3 Capture Balls' },
  { kind: 'item', itemId: 'it_salve', qty: 3, label: '3 Potions' },
  { kind: 'gold', gold: 400, label: '400 Gold' },
  { kind: 'item', itemId: 'it_net', qty: 2, label: '2 Hunter Nets' },
  { kind: 'item', itemId: 'it_poultice', qty: 3, label: '3 Herb Poultices' },
  { kind: 'gold', gold: 750, label: '750 Gold' },
];

/** The reward for a given cycle index (wraps; tolerates negatives/non-ints). */
export function dailyRewardForDay(day: number): DailyRewardDef {
  const n = DAILY_REWARDS.length;
  const i = ((Math.floor(day || 0) % n) + n) % n;
  return DAILY_REWARDS[i];
}

/**
 * Referral reward: a one-time +Gold bonus granted to BOTH the new player and their referrer the
 * first (and only) time an account is created with a valid `ref`. Never re-granted.
 */
export const REFERRAL_REWARD_GOLD = 500;

/** Presence: a player is "online" when their `last_seen` is within this window. */
export const PRESENCE_ONLINE_MS = 2 * 60 * 1000;

// ── Account (player) level: a progression separate from per-monster levels. ──
// XP is earned on battle wins (higher-level zones pay more), and the level follows a triangular
// curve so each level costs a little more than the last.

/** XP granted per win = enemyLevel × this. Wilds (lvl 2-8) ≈ 20-80, Cave/Badlands (8-20) ≈ 80-200. */
export const XP_PER_ENEMY_LEVEL = 10;

/** Player level a player must reach before the Cave (higher-tier zone) unlocks. */
export const CAVE_UNLOCK_LEVEL = 5;

/** Total XP required to BE level `n` (triangular: lvl2=100, lvl3=300, lvl5=1000, lvl8=2800). */
export function xpForLevel(level: number): number {
  const n = Math.max(1, Math.floor(level));
  return 50 * n * (n - 1);
}

/** The account level for a given total XP. Inverse of {@link xpForLevel}; never below 1. */
export function playerLevel(xp: number): number {
  const x = Math.max(0, Number(xp) || 0);
  return Math.max(1, Math.floor((1 + Math.sqrt(1 + x / 12.5)) / 2));
}

/**
 * The fixed Gold Item Shop catalog. Prices are intentionally sinks (heal/revive/balls/buff),
 * with the Gold Vault priced so its +100/day claim takes ~30 days to break even.
 */
export const SHOP_ITEMS: readonly ShopItemDef[] = Object.freeze([
  {
    id: 'si_greater_potion',
    name: 'Greater Potion',
    description: 'Heal a monster ~60 HP.',
    priceGold: 250,
    effect: { kind: 'inventory', itemId: 'it_greater_potion', qty: 1 },
  },
  {
    id: 'si_revive',
    name: 'Revive',
    description: 'Revive one fainted monster.',
    priceGold: 450,
    effect: { kind: 'inventory', itemId: 'it_revive', qty: 1 },
  },
  {
    id: 'si_premium_ball_5',
    name: 'Premium Capture Ball (x5)',
    description: '5 balls with a much better catch rate.',
    priceGold: 400,
    effect: { kind: 'inventory', itemId: 'it_premium_ball', qty: 5 },
  },
  {
    id: 'si_lucky_charm',
    name: 'Lucky Charm',
    description: `+${LUCKY_CHARM_BONUS_PCT}% battle Gold for the next ${LUCKY_CHARM_BATTLES} battles.`,
    priceGold: 1200,
    effect: { kind: 'buff', buff: 'lucky_charm', battles: LUCKY_CHARM_BATTLES },
  },
  {
    id: 'si_gold_vault',
    name: 'Gold Vault',
    description: `One-time unlock: claim +${GOLD_VAULT_CLAIM_AMOUNT} Gold once every 24h.`,
    priceGold: 3000,
    effect: { kind: 'gold_vault' },
  },
]);

/** Look up a catalog item by id (the server re-prices from this — never trusts client prices). */
export function getShopItem(id: string): ShopItemDef | undefined {
  return SHOP_ITEMS.find((i) => i.id === id);
}

// ════════════════════════════════════════════════════════════════════════════
// $TOKEN-POWERED ON-CHAIN BEASTS — mint (cNFT), off-chain stake, $TOKEN spend.
// ════════════════════════════════════════════════════════════════════════════
// All values here are PURE + tunable (no chain imports — this ships to the client
// bundle). On-chain wiring (Bubblegum tree, treasury keypair, RPC) is server-only
// env config and lives in apps/api, never here. A missing config degrades to
// "coming soon" client-side; these constants only describe fees/rates/caps.

/**
 * Beast rarity TIER derived from a species' catchRate (rarer to catch → higher tier).
 * Lowercase + chain-economy-facing (mint fee / stake rate). Distinct from the
 * display-facing {@link Rarity} ('Epic' | …) so we never couple UI copy to economy math.
 */
export type BeastTier = 'common' | 'uncommon' | 'rare' | 'epic';

/**
 * catchRate → tier thresholds (ascending rarity as catchRate falls). A beast you can
 * catch ≥30% of the time is common; <6% is epic. Boundaries are inclusive-lower on the
 * common side: r < 0.06 epic, r < 0.18 rare, r < 0.30 uncommon, else common.
 */
export const BEAST_TIER_BY_CATCHRATE = Object.freeze({
  epic: 0.06,
  rare: 0.18,
  uncommon: 0.3,
});

/** The {@link BeastTier} for a species' catchRate (0..1). Defensive: clamps non-finite → common. */
export function beastTier(catchRate: number): BeastTier {
  const r = Number(catchRate);
  if (!Number.isFinite(r)) return 'common';
  if (r < BEAST_TIER_BY_CATCHRATE.epic) return 'epic';
  if (r < BEAST_TIER_BY_CATCHRATE.rare) return 'rare';
  if (r < BEAST_TIER_BY_CATCHRATE.uncommon) return 'uncommon';
  return 'common';
}

/** Per-tier economic multiplier — scales both mint fee and stake yield. */
export const TIER_MULT: Readonly<Record<BeastTier, number>> = Object.freeze({
  common: 1,
  uncommon: 2,
  rare: 4,
  epic: 8,
});

/** Display rarity label for a tier (maps the lowercase economy tier → {@link Rarity}). */
export function tierToRarity(tier: BeastTier): Rarity {
  switch (tier) {
    case 'epic':
      return 'Epic';
    case 'rare':
      return 'Rare';
    case 'uncommon':
      return 'Uncommon';
    default:
      return 'Common';
  }
}

// ── MINT: turn a caught beast into a compressed NFT (cNFT) for a $TOKEN fee. ──

/** Marketplace royalty on every minted beast cNFT (5%). Encoded in the metadata. */
export const ROYALTY_BPS = 500;

/** Flat base $TOKEN fee (UI units) to mint any beast. */
export const MINT_FEE_BASE_TOKEN = 25;
/** Extra $TOKEN per monster level, so leveled beasts cost a little more to immortalize. */
export const MINT_FEE_PER_LEVEL_TOKEN = 0.5;
/** Of the mint fee, how much is burned vs. sent to treasury (deflationary-leaning). */
export const MINT_FEE_BURN_BPS = 8000; // 80% burned
export const MINT_FEE_TREASURY_BPS = 2000; // 20% treasury

/**
 * Total $TOKEN (UI units) to mint a beast of `tier` at `level`, scaled by the tier
 * multiplier. Pure + deterministic so client preview and server charge always agree.
 */
export function beastMintFeeToken(tier: BeastTier, level: number): number {
  const lvl = Math.max(1, Math.floor(Number(level) || 1));
  const raw = (MINT_FEE_BASE_TOKEN + MINT_FEE_PER_LEVEL_TOKEN * (lvl - 1)) * TIER_MULT[tier];
  return Math.round(raw * 100) / 100;
}

// ── STAKE: lock owned beast-cNFTs to accrue $TOKEN over time (off-chain accrual). ──

/** Base $TOKEN/day a staked beast earns (before tier multiplier). */
export const STAKE_BASE_RATE_TOKEN_PER_DAY = 5;
/** Extra $TOKEN/day per monster level. */
export const STAKE_PER_LEVEL_TOKEN_PER_DAY = 0.2;

/** Max beasts a single wallet may stake at once (keeps emission bounded per player). */
export const STAKE_MAX_BEASTS_PER_WALLET = 6;
/** Minimum lock before a staked beast can be unlocked (24h). */
export const STAKE_MIN_LOCK_MS = 24 * 60 * 60 * 1000;
/** Most $TOKEN a wallet can claim per rolling 24h (anti-drain cap). */
export const STAKE_DAILY_CLAIM_CAP_TOKEN = 500;
/** Cooldown between claims (24h). */
export const STAKE_CLAIM_COOLDOWN_MS = 24 * 60 * 60 * 1000;
/** Pre-allocated reward pool (UI units) — hard cap on total stake emission, no infinite mint. */
export const STAKE_REWARD_POOL_TOKEN = 5_000_000;

/**
 * $TOKEN/day a staked beast of `tier` at `level` accrues, scaled by tier multiplier.
 * Accrual is prorated per-second server-side from this daily rate; the client only previews.
 */
export function beastStakeRatePerDay(tier: BeastTier, level: number): number {
  const lvl = Math.max(1, Math.floor(Number(level) || 1));
  const raw = (STAKE_BASE_RATE_TOKEN_PER_DAY + STAKE_PER_LEVEL_TOKEN_PER_DAY * (lvl - 1)) * TIER_MULT[tier];
  return Math.round(raw * 100) / 100;
}

/** Per-second accrual for a daily rate — the unit the server integrates over elapsed time. */
export function stakeAccrualPerSecond(ratePerDay: number): number {
  return (Number(ratePerDay) || 0) / 86_400;
}

// ── SPEND: pay $TOKEN (alongside Gold) in the card shop + for premium items. ──

/**
 * Peg: how many $TOKEN UI units ≈ $1 of real card value, used to price cards in $TOKEN. Tunable;
 * the server is authoritative and re-derives the price from Gold. Sized against the realistic stake
 * faucet so a card is a reachable multi-day (not multi-month) sink: maxed staking yields ≈400 $TOKEN/
 * day (6 epic beasts), so at 100 the cheapest card (~$9.5 floor) ≈ 950 $TOKEN ≈ ~2-3 days, and a
 * mid ~$95 card ≈ 9500 $TOKEN ≈ ~3-4 weeks. (Was 1000, which — once the peg was fixed — made even
 * the cheapest card ~3 weeks and a mid card ~6 months of maxed staking.)
 */
export const TOKEN_PER_USD = 100;

/**
 * How much Gold one $TOKEN is "worth" for the card-shop peg ({@link cardTokenPrice}). This is the
 * RATIO itself (GOLD_PER_USD / TOKEN_PER_USD = 315/1000 = 0.315), NOT floored — flooring the ratio
 * to 1 collapsed the whole peg to 1:1 (a 3000-Gold card → 3000 $TOKEN instead of the intended
 * ~9524). The floor belongs on the FINAL price only (see cardTokenPrice), never on the conversion.
 */
export const GOLD_PER_TOKEN = GOLD_PER_USD / TOKEN_PER_USD;

/**
 * $TOKEN price for a card given its (already holder-discounted) Gold price. The peg runs THROUGH the
 * Gold price so any Gold discount carries into the $TOKEN price; currencies never swap balances.
 * priceGold / GOLD_PER_TOKEN === (priceGold / GOLD_PER_USD) × TOKEN_PER_USD, i.e. valueUsd × the
 * TOKEN_PER_USD peg. Floored at 1 $TOKEN so a near-free card still costs something.
 */
export function cardTokenPrice(priceGold: number): number {
  const gold = Math.max(0, Number(priceGold) || 0);
  return Math.max(1, Math.round(gold / GOLD_PER_TOKEN));
}

/** Card $TOKEN payments go entirely to treasury (real cards are fulfilled by the team). */
export const CARD_TOKEN_TREASURY_BPS = 10000;

/** Of a premium-item $TOKEN spend, how much is burned vs. treasury (50/50). */
export const PREMIUM_BURN_BPS = 5000;
export const PREMIUM_TREASURY_BPS = 5000;

/** A premium catalog item buyable with $TOKEN. Reuses the in-game effect application. */
export interface PremiumTokenItemDef {
  id: string;
  name: string;
  description: string;
  /** $TOKEN UI-unit price. */
  priceToken: number;
  effect: ShopItemEffect;
}

/**
 * Premium $TOKEN-only catalog (better balls, a daily-spin token). Modest, small-game
 * appropriate. Effects reuse the existing {@link ShopItemEffect} application server-side.
 */
export const PREMIUM_TOKEN_ITEMS: readonly PremiumTokenItemDef[] = Object.freeze([
  {
    id: 'pt_premium_ball_10',
    name: 'Premium Capture Ball (x10)',
    description: '10 high-catch-rate balls, paid in $TOKEN.',
    priceToken: 5,
    effect: { kind: 'inventory', itemId: 'it_premium_ball', qty: 10 },
  },
  {
    id: 'pt_daily_spin',
    name: 'Daily Spin Token',
    description: 'One premium spin on the prize wheel.',
    priceToken: 3,
    effect: { kind: 'inventory', itemId: 'it_spin_token', qty: 1 },
  },
]);

/** Look up a premium $TOKEN item by id (server re-prices from this — never trusts client). */
export function getPremiumTokenItem(id: string): PremiumTokenItemDef | undefined {
  return PREMIUM_TOKEN_ITEMS.find((i) => i.id === id);
}

// ── On-chain metadata: ONE source of truth for the cNFT attributes (api + game preview). ──

/** A single Metaplex-style metadata attribute (trait_type / value). */
export interface MetadataAttribute {
  trait_type: string;
  value: string | number;
}

/**
 * The attribute array for a beast's cNFT metadata, built from its SPECIES + INSTANCE.
 * Pure so the api metadata route and the game's mint-preview render the exact same traits.
 * The server passes authoritative species/instance (from party_json + content), never the client.
 */
export function beastToMetadataAttributes(
  species: Pick<MonsterSpecies, 'name' | 'element' | 'catchRate'>,
  instance: Pick<MonsterInstance, 'level'> & {
    stats?: { hp: number; atk: number; def: number; spd: number };
  },
): MetadataAttribute[] {
  const tier = beastTier(species.catchRate);
  const level = Math.max(1, Math.floor(Number(instance.level) || 1));
  const stats = instance.stats ?? { hp: 0, atk: 0, def: 0, spd: 0 };
  return [
    { trait_type: 'Species', value: species.name },
    { trait_type: 'Element', value: species.element as ElementType },
    { trait_type: 'Level', value: level },
    { trait_type: 'HP', value: Math.round(Number(stats.hp) || 0) },
    { trait_type: 'ATK', value: Math.round(Number(stats.atk) || 0) },
    { trait_type: 'DEF', value: Math.round(Number(stats.def) || 0) },
    { trait_type: 'SPD', value: Math.round(Number(stats.spd) || 0) },
    { trait_type: 'Rarity', value: tierToRarity(tier) },
  ];
}

/** A tombstone: loot dropped on death in a PvP realm, lootable by others for a window. */
export interface Tombstone {
  id: string;
  ownerId: string;
  realm: RealmId;
  pos: { x: number; y: number };
  gold: number;
  items: { itemId: string; qty: number }[];
  expiresAt: string;
}

// ── Limited-time community SPECIAL EVENT (auto-expires; no redeploy needed to turn off) ──
// Window set for the live event on 2026-06-23 (~21:05 → 22:15 UTC). During it: boosted Gold + a higher
// wild-encounter rate, and the client shows a banner. After EVENT_END_MS everything reverts to normal.
export const EVENT_END_MS = 1782252900000; // 2026-06-23 22:15:00 UTC
export const EVENT_GOLD_MULT = 2; // battle Gold ×2 during the event (applied AFTER the normal cap)
export const EVENT_ENCOUNTER_RATE = 0.13; // grass-encounter chance per step during the event (vs 0.05)
/** True while the special event window is open. */
export function isEventActive(now: number = Date.now()): boolean {
  return now < EVENT_END_MS;
}

// ── Daily FARMABLE-Gold cap (battles + map pickups, per rolling 24h, per wallet) ──
// Paces real-card acquisition: sized so ~3 days of capped earning buys a ~$20 card (12 000 Gold at
// GOLD_PER_USD), the community target — and it caps bot farming to the same legit rate per account.
// Quest + daily-login Gold is SEPARATE (granted on top, uncapped). Raised during the special event.
export const DAILY_GOLD_CAP = 4000;
export const DAILY_GOLD_WINDOW_MS = 24 * 60 * 60 * 1000;
/** The effective daily farmable-Gold cap right now (raised ×3 during the special event). */
export function dailyGoldCap(now: number = Date.now()): number {
  return isEventActive(now) ? DAILY_GOLD_CAP * 3 : DAILY_GOLD_CAP;
}
